import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Radio, RadioGroup, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ProjectMember, ProjectStatusEnum, RoleEnum } from '~/api/types';
import {
  useAddMemberMutation,
  useChangeMemberRoleMutation,
  useGetProjectQuery,
  useRemoveMemberMutation,
  useTransferOwnershipMutation,
} from '~/api/v2/hooks';
import { ButtonSpinner, Table } from '~/components';
import { MAX_PAGE_SIZE } from '~/components/Table';
import { useAppSelector } from '~/redux';
import { selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { RemoveMemberCell, UsernameCell } from '.';

const idSanitize = (id: string) => id.replace(/\./g, '§');
const idRestore = (id: string) => id.replace(/§/g, '.');

const ProjectMembersTable = () => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const { data: projectData, isFetching } = useGetProjectQuery({ projectId });
  const projectOwnerId = projectData?.members.find((member) => member.role_id === RoleEnum.OWNER)?.id;
  const [changeMemberRole, { isLoading: isChangingRole }] = useChangeMemberRoleMutation({
    fixedCacheKey: `change-member-role-${projectId}`,
  });
  const [, { isLoading: isTransferingOwnership }] = useTransferOwnershipMutation({
    fixedCacheKey: `transfer-ownership-${projectId}`,
  });
  const [, { isLoading: isRemovingMember }] = useRemoveMemberMutation({
    fixedCacheKey: `remove-member-${projectId}`,
  });
  const [, { isLoading: isAddingMember }] = useAddMemberMutation({
    fixedCacheKey: `add-member-${projectId}`,
  });

  const isLoading = isFetching || isChangingRole || isTransferingOwnership || isRemovingMember || isAddingMember;

  const defaultValues = useMemo(() => {
    if (!projectData?.members) return {};

    return Object.fromEntries(projectData.members.map((member) => [idSanitize(member.id), member.role_id]));
  }, [projectData?.members]);

  const {
    control,
    handleSubmit,
    reset,
    unregister,
    formState: { dirtyFields },
  } = useForm<{ [userId: string]: RoleEnum }>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const saveRoles = async (formData: { [userId: string]: RoleEnum }) => {
    const changedUserIds = Object.keys(dirtyFields);

    await Promise.all(
      Object.entries(formData)
        .filter(([key]) => changedUserIds.includes(key))
        .map(async ([key, value]) => {
          await changeMemberRole({ projectId, user_id: idRestore(key), role_id: +value });
        }),
    );
    reset();
  };

  const columns: GridColDef<ProjectMember>[] = [
    {
      field: 'name',
      headerName: 'User name',
      sortable: false,
      renderCell: (params) => <UsernameCell member={params?.row} />,
      flex: 3,
    },
    {
      field: 'role',
      renderHeader: () => (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="div">Reader</Typography>
          <Typography component="div">Writer</Typography>
        </Box>
      ),
      sortable: false,
      renderCell: (params) => {
        return (
          <Controller
            name={idSanitize(params.row.id)}
            control={control}
            render={({ field }) => {
              const isDisabled =
                RoleEnum.OWNER === params.row.role_id ||
                projectData?.status === ProjectStatusEnum.INACTIVE ||
                isLoading;

              return (
                <RadioGroup
                  row
                  value={field.value}
                  onChange={(e) => field.onChange(+e.target.value)}
                  sx={{ justifyContent: 'space-between', width: '80%' }}
                >
                  <Radio disabled={isDisabled} value={RoleEnum.READER} />
                  <Radio disabled={isDisabled} value={RoleEnum.WRITER} />
                </RadioGroup>
              );
            }}
          />
        );
      },
      flex: 2,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (params) =>
        userId !== params.row.id &&
        params.row.id !== projectOwnerId &&
        projectData?.status !== ProjectStatusEnum.INACTIVE ? (
          <RemoveMemberCell userId={params.row.id} unregister={unregister} disabled={isLoading} />
        ) : null,
      sortable: false,
      flex: 1,
    },
  ];

  if (!projectData?.members) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <form onSubmit={handleSubmit(saveRoles)}>
        <Table
          rows={projectData.members}
          columns={columns}
          loading={isLoading}
          hideFooter
          paginationModel={{ page: 0, pageSize: Math.min(projectData.members.length, MAX_PAGE_SIZE) }}
          sx={{ '& .MuiDataGrid-columnHeaderTitleContainerContent': { width: '80%' } }}
        />
        {!!Object.keys(dirtyFields).length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: '2rem' }}>
            <CustomButton type="submit" disabled={isLoading}>
              Submit Privilege Changes
              {isLoading && <ButtonSpinner />}
            </CustomButton>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default ProjectMembersTable;
